import { convertirAMXN, formatearMoneda, resolverPago, type CorregirCobroInput, type Moneda, type VentaVista } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Input, Select, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';
import { useConfig } from '../agenda/datos';

type Metodo = 'efectivo' | 'transferencia';

interface FilaPago {
  id?: string;
  metodo: Metodo;
  moneda: Moneda;
  monto: string;
  referencia: string;
}

const aCentavos = (texto: string) => Math.round((Number.parseFloat(texto) || 0) * 100);
const aTexto = (centavos: number) => (centavos / 100).toFixed(2);

export const etiquetaPago = (metodo: string, moneda: string) =>
  metodo === 'transferencia' ? `Transferencia ${moneda}` : metodo === 'tarjeta' ? `Tarjeta ${moneda}` : `Efectivo ${moneda}`;

/** Corrige importe de un concepto, formas de pago y monto recibido de una venta pagada hoy. */
export function CorreccionCobroDialog({ venta, abierto, onCerrar }: { venta: VentaVista; abierto: boolean; onCerrar: () => void }) {
  const qc = useQueryClient();
  const tipoCambioActual = useConfig<number>('caja.tipoCambioUSD', 17);
  const [lineaId, setLineaId] = useState('');
  const [importe, setImporte] = useState('');
  const [pagos, setPagos] = useState<FilaPago[]>(() =>
    venta.pagos.map((p) => ({ id: p.id, metodo: p.metodo as Metodo, moneda: p.moneda, monto: aTexto(p.monto), referencia: p.referencia ?? '' })),
  );
  const [motivo, setMotivo] = useState('');
  const [revisando, setRevisando] = useState(false);

  // La propina no se edita aquí, pero los pagos deben seguir cubriéndola.
  const pagadoAntes = venta.pagos.reduce((s, p) => s + p.montoMXN, 0);
  const propinaMXN = Math.max(0, pagadoAntes - venta.cambioMXN - venta.total);
  const linea = venta.lineas.find((l) => l.id === lineaId);
  const importeFinal = linea ? aCentavos(importe) : null;
  const totalNuevo = linea && importeFinal !== null ? venta.total - linea.neto + importeFinal : venta.total;
  const tipoCambio = (p: FilaPago) => {
    if (p.moneda === 'MXN') return 1;
    const anterior = venta.pagos.find((x) => x.id === p.id);
    return anterior?.moneda === p.moneda ? anterior.tipoCambio : tipoCambioActual;
  };
  const entrada = pagos
    .filter((p) => aCentavos(p.monto) > 0)
    .map((p) => ({ metodo: p.metodo, moneda: p.moneda, monto: aCentavos(p.monto), tipoCambio: tipoCambio(p) }));
  const r = resolverPago(totalNuevo + propinaMXN, entrada);
  const errorPago =
    r.faltaMXN > 0 ? `Falta cubrir ${formatearMoneda(r.faltaMXN)}` : !r.valido ? 'La transferencia no puede dar cambio; solo el efectivo excede el total.' : null;
  const listo = motivo.trim().length > 0 && !errorPago && totalNuevo >= 0 && (totalNuevo + propinaMXN === 0 || entrada.length > 0) && (!linea || importe.trim() !== '');

  const corregir = useMutation({
    mutationFn: () => {
      const body: CorregirCobroInput = {
        version: venta.version,
        motivo: motivo.trim(),
        ...(linea && importeFinal !== null ? { ajuste: { lineaId: linea.id, montoFinal: importeFinal } } : {}),
        pagos: pagos
          .filter((p) => aCentavos(p.monto) > 0)
          .map((p) => ({
            ...(p.id ? { id: p.id } : {}),
            metodo: p.metodo,
            moneda: p.metodo === 'transferencia' ? 'MXN' : p.moneda,
            monto: aCentavos(p.monto),
            referencia: p.metodo === 'transferencia' && p.referencia.trim() ? p.referencia.trim() : undefined,
          })) as CorregirCobroInput['pagos'],
      };
      return api<VentaVista>(`/ventas/${venta.id}/cobro`, { method: 'PATCH', body });
    },
    onSuccess: async (v) => {
      toast.success(`Venta ${v.folio} corregida`);
      onCerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['ventas'] }),
        qc.invalidateQueries({ queryKey: ['caja'] }),
        qc.invalidateQueries({ queryKey: ['reportes'] }),
        qc.invalidateQueries({ queryKey: ['nomina'] }),
      ]);
    },
    onError: (e) => {
      setRevisando(false);
      avisarError(e);
    },
  });

  const set = (i: number, cambios: Partial<FilaPago>) => setPagos((l) => l.map((p, j) => (j === i ? { ...p, ...cambios } : p)));

  return (
    <Dialogo
      abierto={abierto}
      onCambiarAbierto={(a) => !a && onCerrar()}
      titulo={`Corregir venta ${venta.folio}`}
      descripcion={`${venta.cliente?.nombre ?? 'Mostrador'} · total actual ${formatearMoneda(venta.total)}`}
      ancho="md"
      pie={
        revisando ? (
          <>
            <Button variante="secundario" onClick={() => setRevisando(false)} disabled={corregir.isPending}>
              Volver
            </Button>
            <Button onClick={() => corregir.mutate()} cargando={corregir.isPending}>
              Confirmar corrección
            </Button>
          </>
        ) : (
          <>
            <Button variante="secundario" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button onClick={() => setRevisando(true)} disabled={!listo}>
              Revisar corrección
            </Button>
          </>
        )
      }
    >
      {revisando ? (
        <div className="grid gap-3 text-[13px] sm:grid-cols-2">
          <section className="rounded-lg border border-border p-3">
            <h3 className="mb-1 font-medium text-foreground-muted">Antes</h3>
            <p className="cifras">Total {formatearMoneda(venta.total)}</p>
            {venta.pagos.map((p) => (
              <p key={p.id} className="cifras">
                {etiquetaPago(p.metodo, p.moneda)} {formatearMoneda(p.monto, p.moneda)}
              </p>
            ))}
            {venta.cambioMXN > 0 && <p className="cifras">Cambio {formatearMoneda(venta.cambioMXN)}</p>}
          </section>
          <section className="rounded-lg border border-accent/40 bg-accent/5 p-3">
            <h3 className="mb-1 font-medium">Después</h3>
            <p className="cifras">Total {formatearMoneda(totalNuevo)}</p>
            {entrada.map((p, i) => (
              <p key={i} className="cifras">
                {etiquetaPago(p.metodo, p.moneda)} {formatearMoneda(p.monto, p.moneda)}
              </p>
            ))}
            {r.cambioMXN > 0 && <p className="cifras">Cambio {formatearMoneda(r.cambioMXN)}</p>}
          </section>
          <p className="text-foreground-muted sm:col-span-2">Motivo: {motivo.trim()}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Concepto a corregir" ayuda="Déjalo vacío si el importe estaba bien">
              {(p) => (
                <Select
                  {...p}
                  value={lineaId}
                  onChange={(e) => {
                    const l = venta.lineas.find((x) => x.id === e.target.value);
                    setLineaId(e.target.value);
                    setImporte(l ? aTexto(l.neto) : '');
                  }}
                >
                  <option value="">Sin cambio de importe</option>
                  {venta.lineas.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.descripcion} · {formatearMoneda(l.neto)}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
            {linea && (
              <Campo etiqueta="Importe correcto" requerido>
                {(p) => <Input {...p} type="number" min="0" step="0.01" inputMode="decimal" value={importe} onChange={(e) => setImporte(e.target.value)} className="cifras" />}
              </Campo>
            )}
          </div>

          <div className="flex items-baseline justify-between rounded-lg bg-muted px-3 py-2 text-[13px]">
            <span>Total corregido</span>
            <span className="cifras font-display text-lg">{formatearMoneda(totalNuevo)}</span>
          </div>
          {propinaMXN > 0 && <p className="-mt-2 text-[12.5px] text-foreground-muted">Los pagos también cubren la propina de {formatearMoneda(propinaMXN)}.</p>}

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[13px] font-medium">Pagos</legend>
            {pagos.map((p, i) => (
              <div key={i} className="flex flex-wrap gap-1.5 rounded-lg border border-border p-2">
                <Select
                  value={`${p.metodo}-${p.moneda}`}
                  onChange={(e) => {
                    const [metodo, moneda] = e.target.value.split('-') as [Metodo, Moneda];
                    set(i, { metodo, moneda });
                  }}
                  aria-label={`Forma de pago ${i + 1}`}
                  className="w-40"
                >
                  <option value="efectivo-MXN">Efectivo MXN</option>
                  <option value="efectivo-USD">Efectivo USD</option>
                  <option value="transferencia-MXN">Transferencia</option>
                </Select>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={p.monto}
                  onChange={(e) => set(i, { monto: e.target.value })}
                  aria-label={`Monto recibido ${i + 1}`}
                  className="cifras min-w-24 flex-1"
                  placeholder="0.00"
                />
                {pagos.length > 1 && (
                  <Button variante="fantasma" tamano="icono" onClick={() => setPagos((l) => l.filter((_, j) => j !== i))} aria-label={`Quitar pago ${i + 1}`}>
                    <Trash2 />
                  </Button>
                )}
                {p.metodo === 'transferencia' && (
                  <Input value={p.referencia} onChange={(e) => set(i, { referencia: e.target.value })} placeholder="Referencia (opcional)" aria-label={`Referencia ${i + 1}`} className="h-8 w-full" />
                )}
                {p.moneda === 'USD' && aCentavos(p.monto) > 0 && (
                  <span className="cifras w-full text-[12px] text-foreground-muted">
                    = {formatearMoneda(convertirAMXN(aCentavos(p.monto), 'USD', tipoCambio(p)))} (TC {tipoCambio(p)})
                  </span>
                )}
              </div>
            ))}
            {pagos.length < 4 && (
              <Button variante="secundario" tamano="sm" onClick={() => setPagos((l) => [...l, { metodo: 'transferencia', moneda: 'MXN', monto: '', referencia: '' }])}>
                <Plus /> Agregar forma de pago
              </Button>
            )}
            <p className="cifras text-[12.5px] text-foreground-muted" aria-live="polite">
              Recibido {formatearMoneda(r.pagadoMXN)} · Cambio {formatearMoneda(r.cambioMXN)}
            </p>
            {errorPago && entrada.length > 0 && (
              <p role="alert" className="text-[12.5px] text-destructive">
                {errorPago}
              </p>
            )}
          </fieldset>

          <Campo etiqueta="Motivo" requerido ayuda="Queda en la auditoría junto con tu nombre">
            {(p) => <Textarea {...p} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} rows={2} />}
          </Campo>
        </div>
      )}
    </Dialogo>
  );
}
